import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ImportNotes from "./components/ImportNotes";
import TagTriggers from "./components/TagTriggers";
import PreviewLaunch from "./components/PreviewLaunch";
import Live from "./components/Live";
import { useNotesStore, getNotesForMatcher } from "./state/notes-store";
import { buildMatcherFromNotes, normalize } from "./logic/trigger-matcher";
import { useUiStore } from "./state/ui-store";
import BackNavOverlay from "./nav/BackNavOverlay";
import AboutPanelOverlay from "./about/AboutPanelOverlay";
import { dlog } from "./utils/debug"; // ← use debug-gated logger

/** ---------- Root redirect logic (SaaS-style home) ---------- */
function RootRedirect() {
  // Always land on Setup/Home when the user hits the root
  return <Navigate to="/setup/import" replace />;
}

/** ---------- Preview test trigger plumbing ---------- */
const handleTestTrigger = (text: string) => {
  const t = (text || "").trim();
  if (!t) return;
  dlog("[TEST TRIGGER] (from Preview input)", t);
  window.dispatchEvent(new CustomEvent("cueboard:test-trigger", { detail: { text: t } }));
};

export default function App() {
  // Cache matcher built from current notes so Preview works even when Live is not mounted
  const matcherRef = useRef<ReturnType<typeof buildMatcherFromNotes> | null>(null);
  const { notes } = useNotesStore();

  // UI store: we still persist the flag, but styling is scoped to Live
  const setStealth = useUiStore((s) => s.setStealth);
  const toggleStealth = useUiStore((s) => s.toggleStealth);

  // Build matcher when notes change (use runtime-expanded notes to avoid UI bloat)
  useEffect(() => {
    matcherRef.current = buildMatcherFromNotes(getNotesForMatcher());
  }, [notes]);

  // Handle Preview "Test Trigger" events
  useEffect(() => {
    const handler = (e: any) => {
      const inputText = (e?.detail?.text ?? "").toString();
      const matcher = matcherRef.current ?? buildMatcherFromNotes(getNotesForMatcher());
      dlog("[TEST TRIGGER] (event)", inputText);
      const normalized = normalize(inputText);
      dlog("[TEST NORMALIZED]", normalized);

      const result = matcher.match(inputText);

      type AnyMatch = {
        cue?: { id?: string; title?: string };
        id?: string;
        title?: string;
        noteId?: string;
        noteTitle?: string;
        trigger?: string;
      };
      const r = (result ?? {}) as AnyMatch;

      const payload = {
        input: inputText,
        matched: Boolean(result),
        noteId: r.cue?.id ?? r.noteId ?? r.id ?? null,
        noteTitle: r.cue?.title ?? r.noteTitle ?? r.title ?? null,
        trigger: r.trigger ?? null,
        result,
        ts: Date.now(),
      };
      const evt = new CustomEvent("cueboard:test-result", { detail: payload });
      window.dispatchEvent(evt);
      dlog("[TEST RESULT payload]", payload);
    };

    window.addEventListener("cueboard:test-trigger", handler);
    return () => window.removeEventListener("cueboard:test-trigger", handler);
  }, [notes]);

  /** ---------- Hydrate persisted presentation flag (optional) ---------- */
  useEffect(() => {
    const raw = localStorage.getItem("cueboard.stealth");
    if (raw === "true") setStealth(true);
  }, [setStealth]);

  /** ---------- Keyboard shortcut: Ctrl+Alt+S (acts only where supported by CSS) ---------- */
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      const editable = (el.getAttribute?.("contenteditable") || "").toLowerCase() === "true";
      return editable || tag === "input" || tag === "textarea" || (el as HTMLInputElement).isContentEditable;
    };

    const onKey = (e: KeyboardEvent) => {
      if (isTyping()) return;
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        toggleStealth();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleStealth]);

  return (
    <BrowserRouter>
      <Routes>
        {/* SaaS-style home: always route root → /setup/import */}
        <Route path="/" element={<RootRedirect />} />

        {/* Setup flow */}
        <Route path="/setup/import" element={<ImportNotes />} />
        <Route path="/setup/tag" element={<TagTriggers />} />
        <Route path="/setup/preview" element={<PreviewLaunch onTestTrigger={handleTestTrigger} />} />

        {/* Live */}
        <Route path="/live" element={<Live />} />

        {/* Unknown paths → home (not Live) */}
        <Route path="*" element={<Navigate to="/setup/import" replace />} />
      </Routes>

      {/* Overlays mounted globally (no edits to page components) */}
      <BackNavOverlay />
      <AboutPanelOverlay />
    </BrowserRouter>
  );
}
